import { useState } from "react";
import { Link, useLocation } from "react-router";
import { ArrowRight, CheckCircle2, Mail, MapPin, MessageCircle } from "lucide-react";
import { Seo } from "@/components/seo";
import { Section } from "@/components/marketing/layout";
import { PricingTable } from "@/components/marketing/pricing-table";
import { Faq } from "@/components/marketing/faq";
import { Button } from "@/components/ui/button";
import { Field, Input, Textarea } from "@/components/ui/input";
import { errorMessage } from "@/lib/api";
import { FEATURES as FLAGS } from "@markfeed/shared";
import { SEO_PAGES } from "@/routes/seo";
import { FinalCta } from "./home";
import { LANDINGS } from "./landing";

function PageSeo() {
  const { pathname } = useLocation();
  const seo = SEO_PAGES[pathname];
  return seo ? <Seo title={seo.title} description={seo.description} path={pathname} /> : null;
}

export function FeaturesIndex() {
  const items: [keyof typeof LANDINGS, string][] = [
    ["website-feedback", "/features/website-feedback"],
    ["client-feedback", "/features/client-feedback"],
    ["task-management", "/features/task-management"],
    ...(FLAGS.mockups ? ([["mockup-feedback", "/features/mockup-feedback"]] as [keyof typeof LANDINGS, string][]) : []),
  ];
  return (
    <>
      <PageSeo />
      <Section page eyebrow="Features" title="Built around one simple loop" intro="Create project → add website → share link → client clicks → fix → resolve → approve. Every feature exists to make that loop faster.">
        <div className="grid gap-4 md:grid-cols-2">
          {items.map(([key, to]) => {
            const c = LANDINGS[key]!;
            return (
              <Link key={to} to={to} className="group rounded-2xl border border-line bg-white p-7 hover:shadow-md">
                <p className="text-[13px] font-semibold uppercase tracking-wider text-brand-600">{c.eyebrow}</p>
                <h2 className="mt-2 text-xl font-semibold group-hover:text-brand-700">{c.title}</h2>
                <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{c.subtitle}</p>
                <ul className="mt-4 space-y-1.5">
                  {c.checklist.slice(0, 3).map((x) => (
                    <li key={x} className="flex gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="mt-0.5 size-4 text-green-600" /> {x}
                    </li>
                  ))}
                </ul>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-brand-600">
                  Learn more <ArrowRight className="size-4" />
                </span>
              </Link>
            );
          })}
        </div>
      </Section>
      <FinalCta />
    </>
  );
}

export function Pricing() {
  return (
    <>
      <PageSeo />
      <section className="mx-auto max-w-6xl px-5 pb-10 pt-16 text-center sm:pt-20">
        <p className="text-[13px] font-semibold uppercase tracking-wider text-brand-600">Pricing</p>
        <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">Fair pricing, in rupees</h1>
        <p className="mx-auto mt-4 max-w-xl text-lg text-slate-600">Start free. Clients are always free and unlimited — you only pay for your team.</p>
      </section>
      <section className="mx-auto max-w-6xl px-5 pb-16">
        <PricingTable />
      </section>
      <section className="border-t border-line bg-white">
        <div className="mx-auto max-w-3xl px-5 py-20">
          <h2 className="text-center text-3xl font-semibold tracking-tight">Pricing questions</h2>
          <div className="mt-10">
            <Faq />
          </div>
        </div>
      </section>
    </>
  );
}

export function About() {
  return (
    <>
      <PageSeo />
      <Section page eyebrow="About" title="Made in India, for the way Indian teams actually work">
        <div className="grid gap-10 text-[17px] leading-relaxed text-slate-600 lg:grid-cols-2">
          <p>
            Every web agency in India knows the drill: the site goes to the client, and feedback comes back as a WhatsApp screenshot, a voice note, a phone call
            and an email — sometimes all four for the same button. Developers lose hours just figuring out what to change.
          </p>
          <p>
            MarkFeed replaces that with one link. Clients click on the exact spot and type what they want. Your team gets a task with the screenshot,
            device and element attached, and the client gets a clear record of what was fixed and approved. Priced in rupees, built for mobile-first clients,
            and designed to stay simple.
          </p>
        </div>
      </Section>
      <FinalCta />
    </>
  );
}

export function Contact() {
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const submit = async () => {
    setState("sending");
    setError(null);
    try {
      const res = await fetch("/api/public/contact", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(form) });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.error?.message ?? "Couldn't send your message");
      setState("sent");
    } catch (e) {
      setError(errorMessage(e));
      setState("idle");
    }
  };
  return (
    <>
      <PageSeo />
      <section className="mx-auto grid max-w-6xl gap-12 px-5 py-16 sm:py-20 lg:grid-cols-[1fr_1.2fr]">
        <div>
          <p className="text-[13px] font-semibold uppercase tracking-wider text-brand-600">Contact</p>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight">Let's talk</h1>
          <p className="mt-4 text-lg text-slate-600">Questions about plans, onboarding your agency, or a custom setup? We usually reply within a working day.</p>
          <ul className="mt-8 space-y-3 text-[15px] text-slate-700">
            <li className="flex items-center gap-3">
              <Mail className="size-5 text-slate-400" /> hello@markfeed.site
            </li>
            <li className="flex items-center gap-3">
              <MessageCircle className="size-5 text-[#25D366]" /> WhatsApp support for paid plans
            </li>
            <li className="flex items-center gap-3">
              <MapPin className="size-5 text-slate-400" /> Bengaluru, India
            </li>
          </ul>
        </div>
        <div className="rounded-2xl border border-line bg-white p-6 sm:p-8">
          {state === "sent" ? (
            <div className="py-10 text-center" role="status">
              <CheckCircle2 className="mx-auto size-10 text-green-600" />
              <h2 className="mt-4 text-xl font-semibold">Thanks, we've got it!</h2>
              <p className="mt-1 text-slate-600">We'll get back to {form.email} soon.</p>
            </div>
          ) : (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Name" htmlFor="ct-name">
                  <Input id="ct-name" required autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </Field>
                <Field label="Email" htmlFor="ct-email">
                  <Input id="ct-email" type="email" required autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </Field>
              </div>
              <Field label="Subject" htmlFor="ct-subject">
                <Input id="ct-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Business plan for 15 people" />
              </Field>
              <Field label="Message" htmlFor="ct-msg">
                <Textarea id="ct-msg" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              </Field>
              {error && (
                <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </p>
              )}
              <Button type="submit" size="lg" loading={state === "sending"}>
                Send message
              </Button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
